import { isListOperator, isRangeOperator, isUnaryOperator } from './schema';
import type { Join, QueryDefinition, QueryField, QueryRule, QueryValue } from './types';

export type QueryMatch = 'all' | 'any';

// "Contact" -> "Contacts", "Status" -> "Statuses". Used for multi-value chip
// summaries like "2 Contacts" - no possessive apostrophe.
export function pluralizeLabel(label: string): string {
  return /[sxz]$|[cs]h$/i.test(label) ? `${label}es` : `${label}s`;
}

export function getQueryMatch(query: QueryDefinition): QueryMatch {
  const groupJoin = query.groups.slice(1).find(group => group.join)?.join;
  const ruleJoin = query.groups
    .flatMap(group => group.rules.slice(1))
    .find(rule => rule.join)?.join;

  return (ruleJoin || groupJoin) === 'OR' ? 'any' : 'all';
}

export function createRule(field: QueryField, join: Join = 'AND'): QueryRule {
  return {
    id: crypto.randomUUID(),
    join,
    field: field.name,
    operator: field.operators[0],
    value: field.defaultValue,
    ...(field.children?.length ? { children: field.children.map(child => createRule(child)) } : {}),
  };
}

export function resetRuleForField(rule: QueryRule, field: QueryField): QueryRule {
  return {
    ...rule,
    field: field.name,
    operator: field.operators[0],
    value: field.defaultValue,
    children: field.children?.map(child => createRule(child)),
  };
}

export function normalizeValue(
  operator: QueryRule['operator'],
  value: QueryValue,
  fieldType?: QueryField['type']
): QueryValue {
  if (isUnaryOperator(operator)) return null;
  if (isRangeOperator(operator)) {
    if (Array.isArray(value) && value.length === 2) return value;
    if ((fieldType === 'date' || fieldType === 'datetime') && typeof value === 'string' && value) {
      return [value, value];
    }
    return ['', ''];
  }
  if (isListOperator(operator))
    return Array.isArray(value) ? value : value === '' || value == null ? [] : [String(value)];
  return Array.isArray(value) ? (value[0] ?? '') : value;
}

export function toApiFilter(query: QueryDefinition) {
  return {
    combinator: 'and' as const,
    groups: query.groups.map(group => ({
      combinator: group.join.toLowerCase(),
      rules: group.rules.map(({ id, join, ...rule }) => {
        void id;
        return { ...rule, combinator: join.toLowerCase() };
      }),
    })),
  };
}

export function validateQuery(query: QueryDefinition, fields: QueryField[]): string[] {
  const fieldMap = new Map(fields.map(field => [field.name, field]));
  return query.groups.flatMap(group =>
    group.rules.flatMap(rule => {
      const field = fieldMap.get(rule.field);
      if (!field) return [`Unknown field: ${rule.field}`];
      if (!field.operators.includes(rule.operator))
        return [`${rule.operator} is not allowed for ${field.label}`];
      if (isUnaryOperator(rule.operator)) return [];
      if (
        Array.isArray(rule.value) &&
        (rule.value.length === 0 || rule.value.some(value => value === ''))
      )
        return [`${field.label} requires a complete value`];
      if (rule.value === '' || rule.value == null) return [`${field.label} requires a value`];
      return [];
    })
  );
}

// Every value currently referenced by rules on a given field, across all groups.
// Used to figure out which selected ids need to be individually resolved when
// they're missing from a field's (paginated/searched) options list.
export function extractRuleValues(query: QueryDefinition, fieldName: string): string[] {
  return query.groups
    .flatMap(group => group.rules)
    .filter(rule => rule.field === fieldName)
    .flatMap(rule => (Array.isArray(rule.value) ? rule.value : [rule.value]))
    .filter((value): value is string => typeof value === 'string' && value !== '');
}

export function filterValidQuery(query: QueryDefinition, fields: QueryField[]): QueryDefinition {
  return {
    groups: query.groups
      .map(group => ({
        ...group,
        rules: group.rules.filter(
          rule => validateQuery({ groups: [{ ...group, rules: [rule] }] }, fields).length === 0
        ),
      }))
      .filter(group => group.rules.length > 0),
  };
}
